import React from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { ContentWrapper, InputWrapper, Error, ButtonsWrapper } from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import { InputPhone } from "../../../../../../components/common/InputPhone";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Button } from "../../../../../../components/common/Button";
import { PHP_BENEFICIARY_TYPES, PHP_DATA_SLICE_KEY, PHP_WALLET_PROVIDERS } from "../const";
import { PhilippinesFormInputs, PhilippinesStepFormProps } from "../types";

const DEFAULT_BENEFICIARY_TYPE = "Individual";

const PhilippinesWalletBeneficiaryForm: React.FC<PhilippinesStepFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: PhilippinesFormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.[PHP_DATA_SLICE_KEY] || {}
  );

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<PhilippinesFormInputs>({
    defaultValues: {
      ...asiaPayoutData,
      beneficiaryType: asiaPayoutData.beneficiaryType || DEFAULT_BENEFICIARY_TYPE,
    },
  });

  const onSubmit: SubmitHandler<PhilippinesFormInputs> = (data) => {
    dispatch(
      savedAsiaPayoutData({ country: PHP_DATA_SLICE_KEY, data: { ...asiaPayoutData, ...data } })
    );
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <InputWrapper>
        <Controller
          name="beneficiaryType"
          control={control}
          rules={{ required: "Beneficiary type is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Beneficiary type"
              options={PHP_BENEFICIARY_TYPES}
              value={field.value || undefined}
              onChange={field.onChange}
              lightBorder
            />
          )}
        />
        {errors?.beneficiaryType?.message && (
          <Error>{String(errors.beneficiaryType.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountName"
          control={control}
          rules={{ required: "Full name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Full name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.accountName}
            />
          )}
        />
        {errors?.accountName?.message && <Error>{String(errors.accountName.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="walletType"
          control={control}
          rules={{ required: "eWallet provider is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="eWallet Provider"
              options={PHP_WALLET_PROVIDERS}
              value={field.value || undefined}
              onChange={field.onChange}
              lightBorder
            />
          )}
        />
        {errors?.walletType?.message && <Error>{String(errors.walletType.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountNumber"
          control={control}
          rules={{
            required: "Account number is required",
            pattern: { value: /^\d+$/, message: "Account number must contain only digits" },
          }}
          render={({ field }) => (
            <Input
              type="text"
              inputMode="numeric"
              label="Account number"
              value={field.value || ""}
              onChange={(event: any) => field.onChange(event.target.value.replace(/\D/g, ""))}
              hasError={!!errors?.accountNumber}
            />
          )}
        />
        {errors?.accountNumber?.message && (
          <Error>{String(errors.accountNumber.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{ required: "Phone number is required" }}
          render={({ field }) => (
            <InputPhone
              label="Phone Number"
              field={field}
              defaultCountry="PH"
              hasError={!!errors?.phoneNumber}
            />
          )}
        />
        {errors?.phoneNumber?.message && <Error>{String(errors.phoneNumber.message)}</Error>}
      </InputWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default PhilippinesWalletBeneficiaryForm;
